import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { MobileShell } from '../components/MobileShell';
import { TopBar } from '../components/TopBar';
import { Badge } from '../components/ui/badge';
import {
  QrCode, MapPin, Calendar, Users, Ship, Map, PartyPopper, Compass,
  Hotel, Car, Receipt, ChevronRight, Loader2, ShoppingBag, Trash2, CalendarDays,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const CATEGORY_ICONS = {
  yacht: Ship, tour: Map, activity: Compass, party: PartyPopper,
  car_rental: Car, lodging: Hotel,
  tours: Map, yachts: Ship, nightlife: PartyPopper, activities: Compass,
};

const STATUS_STYLES = {
  pending_payment: 'bg-rosa text-white',
  partially_paid: 'bg-gold text-foreground',
  confirmed: 'bg-caribbean text-white',
  completed: 'bg-foreground text-background',
  cancelled: 'bg-destructive text-white',
};

const PERIODS = [
  { id: 'all', label_en: 'All', label_es: 'Todas' },
  { id: 'today', label_en: 'Today', label_es: 'Hoy' },
  { id: 'week', label_en: 'This week', label_es: 'Esta semana' },
  { id: 'month', label_en: 'This month', label_es: 'Este mes' },
];

const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
const ymd = (d) => {
  const x = startOfDay(d);
  return x.getFullYear() + '-' + String(x.getMonth()+1).padStart(2,'0') + '-' + String(x.getDate()).padStart(2,'0');
};

export default function BookingsPage() {
  const { api, user } = useAuth();
  const { language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState(searchParams.get('period') || 'all');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDay, setExpandedDay] = useState(null);

  const isProvider = user?.role === 'provider';

  const fetchBookings = () => {
    setLoading(true);
    api().get('/bookings')
      .then((r) => setBookings(r.data || []))
      .catch(() => setBookings([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchBookings(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (period === 'all') setSearchParams({});
    else setSearchParams({ period });
    // eslint-disable-next-line
  }, [period]);

  // Filter by period
  const filtered = useMemo(() => {
    if (period === 'all') return bookings;
    const now = startOfDay(new Date());
    return bookings.filter((b) => {
      const ref = b.date ? new Date(b.date) : new Date(b.created_at);
      if (Number.isNaN(ref.getTime())) return false;
      const refDay = startOfDay(ref);
      if (period === 'today') return refDay.getTime() === now.getTime();
      if (period === 'week') {
        const start = new Date(now); start.setDate(start.getDate() - start.getDay());
        const end = new Date(start); end.setDate(end.getDate() + 7);
        return refDay >= start && refDay < end;
      }
      if (period === 'month') {
        return refDay.getMonth() === now.getMonth() && refDay.getFullYear() === now.getFullYear();
      }
      return true;
    });
  }, [bookings, period]);

  // Group by day
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((b) => {
      const key = b.date || ymd(new Date(b.created_at));
      if (!map[key]) map[key] = [];
      map[key].push(b);
    });
    const days = Object.keys(map).sort((a, b) => (a < b ? 1 : -1));
    return { days, map };
  }, [filtered]);

  // Auto-expand first day when there are no expanded yet
  useEffect(() => {
    if (!expandedDay && grouped.days.length) setExpandedDay(grouped.days[0]);
    // eslint-disable-next-line
  }, [grouped.days.length]);

  const dayCount = (k) => grouped.map[k]?.length || 0;

  const dayTotal = (k) => (grouped.map[k] || []).reduce((sum, b) => {
    return sum + (isProvider ? (b.provider_balance_due ?? 0) : (b.reservation_fee ?? 0));
  }, 0);

  const statusLabel = (s) => ({
    pending_payment: { en: 'Pending', es: 'Pendiente' },
    partially_paid: { en: 'Deposit', es: 'Apartado' },
    confirmed: { en: 'Confirmed', es: 'Confirmada' },
    completed: { en: 'Completed', es: 'Completada' },
    cancelled: { en: 'Cancelled', es: 'Cancelada' },
  }[s]?.[language] || s);

  const formatDayHeader = (key) => {
    try {
      const d = new Date(key);
      if (Number.isNaN(d.getTime())) return key;
      const locale = language === 'en' ? 'en-US' : 'es-MX';
      return d.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
    } catch { return key; }
  };

  const handleHide = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(language === 'en' ? 'Remove this booking from your list?' : '¿Quitar esta reserva de tu lista?')) return;
    try {
      await api().delete(`/bookings/${id}`);
      toast.success(language === 'en' ? 'Removed' : 'Eliminada');
      fetchBookings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const headerSubtitle = isProvider
    ? (language === 'en' ? 'Tap any day to see its tickets.' : 'Toca un día para ver sus tickets.')
    : (language === 'en' ? 'Each booking is a ticket. Show the QR on arrival.' : 'Cada reserva es un ticket. Muestra el QR al llegar.');

  return (
    <MobileShell>
      <TopBar
        showBrand={false}
        title={language === 'en' ? 'My Bookings' : 'Mis Reservas'}
      />

      <div className="px-4 py-4" data-testid="bookings-page">
        <div className="mb-3 flex items-start gap-2 text-xs text-muted-foreground">
          <Receipt size={14} className="mt-0.5 text-caribbean shrink-0" />
          <p>{headerSubtitle}</p>
        </div>

        {/* Period chips */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2 mb-2 sticky top-14 bg-background z-20">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              data-testid={`period-${p.id}`}
              onClick={() => setPeriod(p.id)}
              className={`shrink-0 inline-flex items-center px-3 h-8 rounded-full text-xs font-semibold transition-all ${
                period === p.id ? 'bg-caribbean text-white shadow' : 'bg-muted text-foreground'
              }`}
            >
              {language === 'en' ? p.label_en : p.label_es}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-caribbean" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-border rounded-2xl">
            <ShoppingBag className="mx-auto text-caribbean mb-3" size={28} />
            <p className="text-sm font-medium mb-1">
              {language === 'en' ? 'No bookings in this period' : 'Sin reservas en este período'}
            </p>
            {!isProvider && (
              <Link to="/catalog" className="text-xs text-caribbean font-medium">
                {language === 'en' ? 'Browse catalog →' : 'Ver catálogo →'}
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-3" data-testid="bookings-grouped">
            {grouped.days.map((dayKey) => {
              const isOpen = expandedDay === dayKey;
              const dayBookings = grouped.map[dayKey] || [];
              const total = dayTotal(dayKey);
              return (
                <div key={dayKey} className="bg-card border border-border rounded-2xl overflow-hidden">
                  {/* Day header */}
                  <button
                    type="button"
                    data-testid={`day-${dayKey}`}
                    onClick={() => setExpandedDay(isOpen ? null : dayKey)}
                    className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
                      isOpen ? 'bg-caribbean/10' : 'hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                        isOpen ? 'bg-caribbean text-white' : 'bg-muted text-caribbean'
                      }`}>
                        <CalendarDays size={18} />
                      </div>
                      <div>
                        <p className="font-semibold text-sm capitalize leading-tight">
                          {formatDayHeader(dayKey)}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {dayCount(dayKey)} {language === 'en' ? (dayCount(dayKey) === 1 ? 'booking' : 'bookings') : (dayCount(dayKey) === 1 ? 'reserva' : 'reservas')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-muted-foreground">
                        {isProvider
                          ? (language === 'en' ? 'Collect' : 'Cobras')
                          : (language === 'en' ? 'Reserved' : 'Reservé')}
                      </p>
                      <p className="font-heading font-bold text-caribbean text-base">${total.toFixed(2)}</p>
                    </div>
                  </button>

                  {/* Tickets */}
                  {isOpen && (
                    <div className="divide-y divide-border" data-testid={`day-tickets-${dayKey}`}>
                      {dayBookings.map((b) => {
                        const Icon = CATEGORY_ICONS[b.category] || Compass;
                        return (
                          <div key={b.id} className="relative">
                            <Link
                              to={`/dashboard/bookings/${b.id}`}
                              data-testid={`booking-ticket-${b.id}`}
                              className="block active:scale-[0.99] transition-transform"
                            >
                              {/* Photo + content */}
                              <div className="relative h-24 bg-gradient-to-br from-caribbean to-rosa">
                                {b.product_photo ? (
                                  <img src={b.product_photo} alt="" className="absolute inset-0 w-full h-full object-cover" />
                                ) : (
                                  <div className="absolute inset-0 flex items-center justify-center text-white/40">
                                    <Icon size={42} />
                                  </div>
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/65 to-transparent" />
                                <div className="absolute bottom-2 left-3 right-3 flex items-end justify-between">
                                  <div className="text-white min-w-0 mr-2">
                                    <p className="font-heading font-bold text-base leading-tight line-clamp-1">
                                      {b.product_title || (language === 'en' ? 'Custom experience' : 'Experiencia personalizada')}
                                    </p>
                                    <p className="text-[11px] text-white/85 capitalize">{b.category?.replaceAll('_', ' ')}</p>
                                  </div>
                                  <Badge className={`${STATUS_STYLES[b.status] || 'bg-muted'} text-[10px] border-0 shrink-0`}>
                                    {statusLabel(b.status)}
                                  </Badge>
                                </div>
                              </div>

                              <div className="p-3 flex items-center gap-3">
                                {b.qr_code && (
                                  <div className="bg-white p-1 rounded-lg shrink-0 border border-border">
                                    <QRCodeSVG value={b.qr_code} size={44} bgColor="#ffffff" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0 text-[11px] text-muted-foreground space-y-0.5">
                                  <p className="flex items-center gap-1">
                                    <MapPin size={11} /> {b.location?.replaceAll('_', ' ') || '—'}
                                  </p>
                                  {b.time && (
                                    <p className="flex items-center gap-1">
                                      <Calendar size={11} /> {b.time}
                                    </p>
                                  )}
                                  {b.people_count && (
                                    <p className="flex items-center gap-1">
                                      <Users size={11} /> {b.people_count} {language === 'en' ? 'people' : 'personas'}
                                    </p>
                                  )}
                                </div>
                                <div className="text-right shrink-0">
                                  {isProvider ? (
                                    <>
                                      <p className="text-[9px] text-muted-foreground uppercase tracking-wide">{language === 'en' ? 'Collect' : 'Cobrar'}</p>
                                      <p className="font-heading font-bold text-caribbean text-base">${b.provider_balance_due ?? b.total_price}</p>
                                    </>
                                  ) : (
                                    <>
                                      <p className="text-[9px] text-muted-foreground uppercase tracking-wide">{language === 'en' ? 'Reserved' : 'Reservé'}</p>
                                      <p className="font-heading font-bold text-caribbean text-base">${b.reservation_fee ?? b.total_price}</p>
                                    </>
                                  )}
                                  <p className="text-[10px] text-muted-foreground">USD</p>
                                </div>
                              </div>

                              <div className="px-3 pb-3 flex items-center justify-between text-[11px] text-caribbean font-semibold">
                                <span className="inline-flex items-center gap-1"><QrCode size={12} /> {b.qr_code}</span>
                                <ChevronRight size={14} />
                              </div>
                            </Link>

                            {/* Delete button (tourist + cancelled) */}
                            {!isProvider && b.status === 'cancelled' && (
                              <button
                                type="button"
                                data-testid={`delete-booking-${b.id}`}
                                onClick={(e) => handleHide(e, b.id)}
                                className="absolute top-2 right-2 w-9 h-9 rounded-full bg-black/50 backdrop-blur text-white flex items-center justify-center hover:bg-destructive transition-colors"
                                title={language === 'en' ? 'Remove from list' : 'Quitar de mi lista'}
                              >
                                <Trash2 size={15} />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </MobileShell>
  );
}
